import { useEffect, useRef, useState } from "react";
import { getApi } from "@/api";
import type { StatusResponse } from "@/types/api";

interface UseReportStatusState {
  data: StatusResponse | null;
  loading: boolean;
  error: string | null;
}

interface UseReportStatusOptions {
  /** Keep polling until paymentState is paid (for post-checkout success page). */
  pollUntilPaid?: boolean;
}

const DEFAULT_POLL_MS = 5000;

function shouldKeepPolling(data: StatusResponse, options?: UseReportStatusOptions): boolean {
  if (data.status === "processing") return true;
  if (options?.pollUntilPaid && data.paymentState !== "paid" && data.status !== "failed") return true;
  return false;
}

/** Polls report status until processing completes (and optionally until paid). */
export function useReportStatus(
  reportId: string | undefined,
  options?: UseReportStatusOptions,
): UseReportStatusState {
  const [state, setState] = useState<UseReportStatusState>({
    data: null,
    loading: true,
    error: null,
  });
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!reportId) {
      setState({ data: null, loading: false, error: "Missing report id." });
      return;
    }

    let cancelled = false;
    const api = getApi();

    const poll = async () => {
      try {
        const data = await api.getStatus(reportId);
        if (cancelled) return;
        setState({ data, loading: false, error: null });
        if (shouldKeepPolling(data, options)) {
          timer.current = window.setTimeout(poll, data.retryAfterMs ?? DEFAULT_POLL_MS);
        }
      } catch (e) {
        if (cancelled) return;
        const message =
          e && typeof e === "object" && "message" in e
            ? String((e as Error).message)
            : "Could not load report status.";
        setState((prev) => ({ ...prev, loading: false, error: message }));
        timer.current = window.setTimeout(poll, DEFAULT_POLL_MS);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [reportId, options?.pollUntilPaid]);

  return state;
}
